import React, { useState } from 'react';
import { Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { LayoutDashboard, FolderKanban, MessageSquare, Settings, LogOut, Home, Info, ShieldCheck, Image as ImageIcon, Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

export default function AdminLayout() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!currentUser) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error', error);
    }
  };

  const navItems = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Gösterge Paneli' },
    { path: '/admin/homepage', icon: <Home size={20} />, label: 'Ana Sayfa Düzenle' },
    { path: '/admin/about', icon: <Info size={20} />, label: 'Kurumsal (Hakkımızda)' },
    { path: '/admin/services', icon: <ShieldCheck size={20} />, label: 'Hizmetlerimiz' },
    { path: '/admin/gallery', icon: <ImageIcon size={20} />, label: 'Galeri Yönetimi' },
    { path: '/admin/floorplans', icon: <FolderKanban size={20} />, label: 'Kat Planları Yönetimi' },
    { path: '/admin/projects', icon: <FolderKanban size={20} />, label: 'Projeler' },
    { path: '/admin/catalog', icon: <FolderKanban size={20} />, label: '3D Katalog Yönetimi' },
    { path: '/admin/partners', icon: <FolderKanban size={20} />, label: 'Çözüm Ortakları' },
    { path: '/admin/contact-settings', icon: <Settings size={20} />, label: 'İletişim & Harita' },
    { path: '/admin/messages', icon: <MessageSquare size={20} />, label: 'Mesaj Kutusu' },
  ];

  const SidebarContent = () => (
    <>
        <div className="p-6 pb-8 border-b border-zinc-800 flex flex-col items-center relative">
          <img 
            src="https://static.readdy.ai/image/462a574349d19c517babfc55bf63e079/c8ac21bc970c8b7e4f4c5b80839b9d5d.png" 
            alt="Logo" 
            className="w-24 brightness-0 invert opacity-90 mb-4"
          />
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Yönetim Merkezi</div>
          
          <button 
             onClick={() => setIsMobileMenuOpen(false)}
             className="md:hidden absolute top-4 right-4 text-zinc-400 hover:text-white"
          >
             <X size={24} />
          </button>
        </div>

        <nav className="flex-1 py-6 px-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname.includes(item.path);
            return (
              <Link 
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive 
                    ? 'bg-red-600/10 text-red-500 font-bold border border-red-600/20' 
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
              >
                {item.icon}
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-zinc-800 mt-auto">
          <button 
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-zinc-400 hover:bg-red-600/10 hover:text-red-500 transition-colors"
          >
            <LogOut size={20} />
            <span className="text-sm font-bold">Güvenli Çıkış</span>
          </button>
        </div>
    </>
  );

  return (
    <div className="min-h-screen bg-zinc-950 flex font-sans text-zinc-300 relative">
      
      {/* Desktop Sidebar */}
      <aside className="w-64 bg-zinc-900 border-r border-zinc-800 flex flex-col hidden md:flex sticky top-0 h-screen">
         <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
           <>
             <motion.div 
               initial={{ opacity: 0 }} 
               animate={{ opacity: 1 }} 
               exit={{ opacity: 0 }} 
               onClick={() => setIsMobileMenuOpen(false)}
               className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
             />
             <motion.aside 
               initial={{ x: '-100%' }}
               animate={{ x: 0 }}
               exit={{ x: '-100%' }}
               transition={{ type: 'spring', damping: 25, stiffness: 200 }}
               className="md:hidden fixed top-0 left-0 h-screen w-64 bg-zinc-900 border-r border-zinc-800 flex flex-col z-50 shadow-2xl"
             >
                <SidebarContent />
             </motion.aside>
           </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Topbar */}
        <header className="h-16 md:h-20 border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-30 px-4 md:px-8 flex items-center justify-between">
          
          <div className="flex items-center gap-3">
             <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden text-zinc-400 hover:text-white mr-2"
             >
                <Menu size={24} />
             </button>
             <h1 className="text-lg md:text-xl font-bold text-white tracking-wide truncate max-w-[200px] sm:max-w-none" style={{ fontFamily: 'Raleway, sans-serif' }}>
               {navItems.find(i => location.pathname.includes(i.path))?.label || 'KARE YAPI Yönetim'}
             </h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-bold text-white">Sistem Yöneticisi</div>
              <div className="text-xs text-green-500 flex items-center gap-1 justify-end">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div> Aktif
              </div>
            </div>
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-bold border border-red-500 shadow-lg shadow-red-600/20">
              Y
            </div>
          </div>
        </header>

        {/* Dynamic Outlet */}
        <div className="flex-1 overflow-auto p-4 md:p-8 bg-zinc-950">
           <Outlet />
        </div>
      </main>
      
    </div>
  );
}
